import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Alert, Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import NavBar from '../NavBar/NavBar';
import SystemBannerFromGrid from '../../LandingHero/SystemBannerFromGrid';
import { PORTAL_SHELL_DEPARTMENT_ID, VIEW_DETAILS_QUERY, decodeDetailsFromQuery, isEmbedUrlAllowed, resolveToAbsoluteUrl, } from '../../../utils/permitDetailsEmbed';
import { useEnvConfig } from '../../../utils/useEnvConfig';
import { useFlowViewerHost } from '../FlowViewer/FlowViewerHost';
const PermitDetailsViewPage = ({ nav, systemBanner }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { departmentId } = useParams();
    const [searchParams] = useSearchParams();
    const viewDetailsParam = searchParams.get(VIEW_DETAILS_QUERY);
    /** Carried forward from the "View Details" click on DepartmentPermitsPage — scopes the agency/permit
     *  banners below; the embed URL alone has no permit/agency identity to derive them from. */
    const agencyFromQuery = searchParams.get('agency')?.trim() || undefined;
    const permitTitleFromQuery = searchParams.get('permitTitle')?.trim() || undefined;
    const embedSrc = useMemo(() => {
        if (!viewDetailsParam)
            return '';
        return resolveToAbsoluteUrl(decodeDetailsFromQuery(viewDetailsParam));
    }, [viewDetailsParam]);
    const isPortalShell = departmentId === PORTAL_SHELL_DEPARTMENT_ID;
    const backHref = isPortalShell ? '/' : departmentId ? `/permits/${departmentId}` : '/permits';
    const backLabel = isPortalShell ? 'Back to Home' : 'Back';
    const env = useEnvConfig();
    const embedValid = Boolean(embedSrc && env !== undefined && isEmbedUrlAllowed(embedSrc, env));
    const host = useFlowViewerHost();
    const slotRef = useRef(null);
    useEffect(() => {
        if (!viewDetailsParam || env === undefined)
            return;
        const abs = resolveToAbsoluteUrl(decodeDetailsFromQuery(viewDetailsParam));
        if (!abs || !isEmbedUrlAllowed(abs, env)) {
            navigate(backHref, { replace: true });
        }
    }, [viewDetailsParam, env, navigate, backHref]);
    /**
     * Reuse the app's pre-booted Flow Viewer iframe: position it over this slot and navigate it to `embedSrc`.
     * Re-runs when the permit changes (embedSrc) but does NOT release in between — so switching permits is an
     * in-place hash swap on the same warm frame, never a fade-out of the previous permit under the new one.
     */
    useLayoutEffect(() => {
        if (!host || !embedValid)
            return;
        const el = slotRef.current;
        if (!el)
            return;
        // Layout effect (not passive) so the route/reveal state is committed BEFORE the browser paints —
        // avoids a one-frame paint of the previous permit on switch.
        host.present(el, embedSrc);
    }, [host, embedValid, embedSrc]);
    /** Release only when leaving the details page entirely (unmount) — keeps the iframe booted for the next open. */
    useEffect(() => {
        return () => host?.release();
    }, [host]);
    // if (!departmentId) {
    //   return (
    //     <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
    //       <Alert severity="warning">Missing department in the URL.</Alert>
    //     </Box>
    //   );
    // }
    return (_jsxs(Box, { component: "main", sx: {
            height: '100vh',
            '@supports (height: 100dvh)': {
                height: '100dvh',
            },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: isPortalShell ? alpha(theme.palette.primary.main, 0.04) : '#fff',
        }, children: [_jsx(Box, { sx: { width: '100%', flexShrink: 0 }, children: _jsx(NavBar, { ...(nav ?? { links: [] }), beforeLoginLink: { href: backHref, label: backLabel } }) }), systemBanner ? (_jsx(SystemBannerFromGrid, { ...systemBanner, scopes: [
                    {},
                    ...(agencyFromQuery ? [{ agencyName: agencyFromQuery }] : []),
                    ...(permitTitleFromQuery ? [{ permitTitle: permitTitleFromQuery }] : []),
                ] })) : null, _jsxs(Box, { sx: {
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                }, children: [viewDetailsParam && env !== undefined && !embedValid ? (_jsx(Alert, { severity: "error", sx: { m: 2, flexShrink: 0 }, children: "This details URL is not valid or cannot be shown here." })) : null, embedValid ? (_jsx(Box, { sx: {
                            flex: 1,
                            minHeight: 0,
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            boxSizing: 'border-box',
                            ...(isPortalShell
                                ? {
                                    // Slim left/right padding so the embedded dashboard gets the full width.
                                    px: { xs: 0.75, sm: 1, md: 1.25 },
                                    pt: { xs: 1.25, sm: 1.75 },
                                    pb: { xs: 2, sm: 2.5 },
                                    background: `linear-gradient(
                      152deg,
                      ${alpha(theme.palette.primary.main, 0.28)} 0%,
                      ${alpha(theme.palette.secondary.main, 0.22)} 42%,
                      ${alpha(theme.palette.primary.dark, 0.14)} 88%,
                      ${alpha(theme.palette.secondary.dark ?? theme.palette.secondary.main, 0.12)} 100%
                    )`,
                                }
                                : { bgcolor: 'grey.50' }),
                        }, children: _jsx(Box, { ref: slotRef, sx: {
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: isPortalShell ? { xs: 2, sm: 2.5 } : 0,
                                overflow: 'hidden',
                                ...(isPortalShell
                                    ? {
                                        /** Frosted frame so the shell gradient reads through instead of a solid white panel. */
                                        bgcolor: alpha(theme.palette.common.white, 0.14),
                                        WebkitBackdropFilter: 'saturate(150%) blur(20px)',
                                        backdropFilter: 'saturate(150%) blur(20px)',
                                        border: `1px solid ${alpha(theme.palette.common.white, 0.42)}`,
                                        boxShadow: `0 10px 40px ${alpha(theme.palette.primary.dark, 0.12)}, 0 2px 12px ${alpha(theme.palette.secondary.main, 0.08)}`,
                                    }
                                    : { bgcolor: 'background.paper' }),
                            }, children: !host ? (_jsx("iframe", { title: isPortalShell ? 'Dashboard' : 'Permit details', src: embedSrc, style: {
                                    border: 'none',
                                    display: 'block',
                                    width: '100%',
                                    height: '100%',
                                    flex: 1,
                                    minHeight: 0,
                                } })) : null }) })) : null] })] }));
};
export default PermitDetailsViewPage;
